import React from 'react';
import { FileText } from 'lucide-react';

import { Badge } from 'components/ui/badge';
import { Card, CardContent } from 'components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import type {
  OrganizationAdminResponseAggregate,
  OrganizationAdminResponseDistributionRow,
  OrganizationAdminResponseQuestion,
} from 'types';

import { getQuestionContext, getQuestionTypeBadgeClassName, getTypeLabel } from './reporting-utils';

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

const CompactDistributionRows: React.FC<{
  rows: OrganizationAdminResponseDistributionRow[];
  aggregateQuestionId: number;
  labelWidthClassName?: string;
  countWidthClassName?: string;
  barClassName?: string;
  barTrackClassName?: string;
  barHeightClassName?: string;
}> = ({
  rows,
  aggregateQuestionId,
  labelWidthClassName = 'w-28',
  countWidthClassName = 'w-[4.5rem]',
  barClassName = 'bg-foundation-base-darkbase-dark-500',
  barTrackClassName = 'bg-foundation-calm-blue-50',
  barHeightClassName = 'h-2',
}) => {
  if (!rows.length) {
    return <p className="text-xs text-muted-foreground">No completed responses yet.</p>;
  }

  return (
    <div
      className="space-y-2.5"
      data-testid={`response-aggregate-distribution-${aggregateQuestionId}`}
    >
      <TooltipProvider>
        {rows.map((row) => (
          <div
            key={`${String(row.bucket_value)}-${row.position ?? 'base'}`}
            className="flex items-center gap-2.5 text-xs"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={`shrink-0 truncate text-foreground ${labelWidthClassName}`}
                  title={row.label}
                >
                  {row.label}
                </span>
              </TooltipTrigger>
              <TooltipContent>{row.label}</TooltipContent>
            </Tooltip>
            <div className={`min-w-0 flex-1 overflow-hidden rounded-full ${barTrackClassName} ${barHeightClassName}`}>
              <div
                className={`h-full rounded-full ${barClassName}`}
                style={{ width: `${clampPercent(row.percent)}%` }}
              />
            </div>
            <span className={`shrink-0 text-right text-muted-foreground ${countWidthClassName}`}>
              {row.count} ({Math.round(row.percent)}%)
            </span>
          </div>
        ))}
      </TooltipProvider>
    </div>
  );
};

const AggregateCardShell: React.FC<{
  aggregate: OrganizationAdminResponseAggregate;
  question?: OrganizationAdminResponseQuestion;
  children: React.ReactNode;
}> = ({ aggregate, question, children }) => (
  <Card disableAnimation maxWidth="none" className="overflow-hidden bg-white p-0 shadow-elevation-sm">
    <CardContent variant="custom" className="space-y-4 p-4">
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <h3 className="line-clamp-3 text-sm font-medium leading-relaxed text-foreground">
            {aggregate.question_text}
          </h3>
          <Badge
            variant="outline"
            className={`shrink-0 text-[11px] ${getQuestionTypeBadgeClassName(aggregate.question_type)}`}
            data-testid={`response-aggregate-type-badge-${aggregate.question_id}`}
          >
            {getTypeLabel(aggregate.question_type)}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {aggregate.respondent_count} respondents
        </p>
        {question ? (
          <p className="line-clamp-2 text-[11px] text-muted-foreground">
            {getQuestionContext(question)}
          </p>
        ) : null}
      </div>
      {children}
    </CardContent>
  </Card>
);

const RatingCard: React.FC<{
  aggregate: OrganizationAdminResponseAggregate;
  question?: OrganizationAdminResponseQuestion;
}> = ({ aggregate, question }) => (
  <AggregateCardShell aggregate={aggregate} question={question}>
    <div className="space-y-3">
      <div className="flex items-baseline gap-2 rounded-2xl bg-foundation-orange-50 px-3 py-3">
        <span
          className="text-3xl font-bold text-foreground"
          data-testid={`response-rating-average-${aggregate.question_id}`}
        >
          {aggregate.average_rating ?? 'N/A'}
        </span>
        <span className="text-xs text-muted-foreground">/ 7 average</span>
      </div>
      <CompactDistributionRows
        rows={aggregate.distribution}
        aggregateQuestionId={aggregate.question_id}
        labelWidthClassName="w-4"
        countWidthClassName="w-[4.5rem]"
      />
    </div>
  </AggregateCardShell>
);

const ChoiceCard: React.FC<{
  aggregate: OrganizationAdminResponseAggregate;
  question?: OrganizationAdminResponseQuestion;
}> = ({ aggregate, question }) => (
  <AggregateCardShell aggregate={aggregate} question={question}>
    <CompactDistributionRows
      rows={aggregate.distribution}
      aggregateQuestionId={aggregate.question_id}
      labelWidthClassName="w-28"
    />
  </AggregateCardShell>
);

const RankingCard: React.FC<{
  aggregate: OrganizationAdminResponseAggregate;
  question?: OrganizationAdminResponseQuestion;
}> = ({ aggregate, question }) => {
  const rankOneRows = aggregate.distribution.filter((row) => row.position === 1);

  return (
    <AggregateCardShell aggregate={aggregate} question={question}>
      <div className="space-y-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Rank 1
        </p>
        <CompactDistributionRows
          rows={rankOneRows}
          aggregateQuestionId={aggregate.question_id}
          labelWidthClassName="w-24"
          barClassName="bg-foundation-base-darkbase-dark-500"
          barTrackClassName="bg-foundation-calm-blue-50"
          barHeightClassName="h-1.5"
        />
      </div>
    </AggregateCardShell>
  );
};

const TextLikeCard: React.FC<{
  aggregate: OrganizationAdminResponseAggregate;
  question?: OrganizationAdminResponseQuestion;
}> = ({ aggregate, question }) => (
  <AggregateCardShell aggregate={aggregate} question={question}>
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-2xl bg-foundation-calm-blue-50 px-3 py-2.5">
        <FileText className="size-4 shrink-0 text-muted-foreground" />
        <p className="text-xs leading-5 text-foreground">
          Recent responses
        </p>
      </div>
      {aggregate.excerpts.length ? (
        <div
          className="h-44 space-y-2 overflow-y-auto pr-1"
          data-testid={`response-aggregate-excerpts-scroll-${aggregate.question_id}`}
        >
          {aggregate.excerpts.map((excerpt) => (
            <div
              key={excerpt.response_id}
              className="rounded-2xl border border-border bg-muted/20 px-3 py-2.5"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {excerpt.display_name}
              </p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm leading-5 text-foreground">
                {excerpt.response_display}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No response excerpts available.</p>
      )}
    </div>
  </AggregateCardShell>
);

const AggregateCard: React.FC<{
  aggregate: OrganizationAdminResponseAggregate;
  question?: OrganizationAdminResponseQuestion;
}> = ({ aggregate, question }) => {
  if (aggregate.question_type === 'rating') {
    return <RatingCard aggregate={aggregate} question={question} />;
  }

  if (['multiple_choice', 'multiple_select', 'boolean'].includes(aggregate.question_type)) {
    return <ChoiceCard aggregate={aggregate} question={question} />;
  }

  if (aggregate.question_type === 'drag_and_drop_ranking') {
    return <RankingCard aggregate={aggregate} question={question} />;
  }

  if (['text_or_audio', 'callback_summary', 'text_list'].includes(aggregate.question_type)) {
    return <TextLikeCard aggregate={aggregate} question={question} />;
  }

  return null;
};

interface AggregateCardsProps {
  questions: OrganizationAdminResponseQuestion[];
  aggregatesByQuestionId: Record<number, OrganizationAdminResponseAggregate>;
  loadingQuestionIds: number[];
  exportAction?: React.ReactNode;
}

const AggregateCardSkeleton: React.FC<{
  question: OrganizationAdminResponseQuestion;
}> = ({ question }) => (
  <Card
    disableAnimation
    maxWidth="none"
    className="overflow-hidden bg-white p-0 shadow-elevation-sm"
    data-testid={`response-aggregate-skeleton-${question.id}`}
  >
    <CardContent variant="custom" className="space-y-4 p-4">
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="h-10 flex-1 animate-pulse rounded-lg bg-muted" />
          <div className="h-5 w-20 animate-pulse rounded-full bg-muted" />
        </div>
        <div className="h-3 w-24 animate-pulse rounded-full bg-muted" />
        <div className="h-3 w-40 animate-pulse rounded-full bg-muted" />
      </div>
      <div className="space-y-2.5">
        <div className="h-14 animate-pulse rounded-2xl bg-foundation-orange-50" />
        {[0, 1, 2].map((index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="h-3 w-10 animate-pulse rounded-full bg-muted" />
            <div className="h-2 flex-1 animate-pulse rounded-full bg-foundation-calm-blue-50" />
            <div className="h-3 w-10 animate-pulse rounded-full bg-muted" />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const AggregateCards: React.FC<AggregateCardsProps> = ({
  questions,
  aggregatesByQuestionId,
  loadingQuestionIds,
  exportAction,
}) => {
  const loadingIds = new Set(loadingQuestionIds);

  return (
    <section aria-labelledby="response-aggregate-heading" className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 id="response-aggregate-heading" className="text-2xl font-semibold text-foreground">
            Aggregate Summary
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Organization-wide results use the latest completed response per user per question.
          </p>
        </div>
        {exportAction ? (
          <div className="shrink-0">
            {exportAction}
          </div>
        ) : null}
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="response-aggregate-grid">
        {questions.map((question) => {
          const aggregate = aggregatesByQuestionId[question.id];
          if (aggregate) {
            return (
              <AggregateCard
                key={question.id}
                aggregate={aggregate}
                question={question}
              />
            );
          }
          if (loadingIds.has(question.id)) {
            return <AggregateCardSkeleton key={question.id} question={question} />;
          }
          return null;
        })}
      </div>
    </section>
  );
};
